import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, ArrowUpRight, DollarSign, Activity, CheckCircle2, Wallet, BarChart3, Clock, Globe, Zap } from 'lucide-react';

const colorMap = {
  primary: { bg: 'bg-[hsl(262_83%_58%/0.2)]', text: 'text-[hsl(var(--primary))] drop-shadow-[0_0_6px_hsl(var(--primary))]' },
  glow: { bg: 'bg-[hsl(266_77%_70%/0.2)]', text: 'text-[hsl(var(--primary-glow))] drop-shadow-[0_0_6px_hsl(var(--primary-glow))]' },
  success: { bg: 'bg-[hsl(160_84%_39%/0.2)]', text: 'text-[hsl(var(--success))] drop-shadow-[0_0_6px_hsl(var(--success))]' },
  accent: { bg: 'bg-[hsl(292_76%_62%/0.2)]', text: 'text-[hsl(var(--neon-accent))] drop-shadow-[0_0_6px_hsl(var(--neon-accent))]' },
};

const statusBase = {
  completed: 'bg-[hsl(var(--success)/0.2)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.4)]',
  pending: 'bg-[hsl(var(--warning)/0.2)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]',
};

const cryptoColors = { USDT: 'text-[hsl(var(--success))]', BTC: 'text-[hsl(var(--warning))]', ETH: 'text-[hsl(var(--primary-glow))]' };

const chartValues = [65, 82, 45, 93, 78, 55, 88];

export const DashboardPreview = () => {
  const { t } = useTranslation();

  const kpis = [
    { label: t('dashboard.kpi_volume'), value: '$48,352.80', change: '+12.5%', trend: 'up', icon: DollarSign, color: 'primary' },
    { label: t('dashboard.kpi_transactions'), value: '1,284', change: '+8.2%', trend: 'up', icon: Activity, color: 'glow' },
    { label: t('dashboard.kpi_success'), value: '99.7%', change: '+0.3%', trend: 'up', icon: CheckCircle2, color: 'success' },
    { label: t('dashboard.kpi_balance'), value: '$12,840.00', change: '-2.1%', trend: 'down', icon: Wallet, color: 'accent' },
  ];

  const days = t('dashboard.days', { returnObjects: true });

  const transactions = [
    { id: 'TX-8F2A', type: 'USDT', amount: '+$2,500.00', status: 'completed', time: '2 min', network: 'TRON' },
    { id: 'TX-4C1B', type: 'BTC', amount: '+$1,850.00', status: 'completed', time: '5 min', network: 'Bitcoin' },
    { id: 'TX-9D3E', type: 'ETH', amount: '+$950.00', status: 'pending', time: '8 min', network: 'Ethereum' },
    { id: 'TX-2A7F', type: 'USDT', amount: '+$3,200.00', status: 'completed', time: '12 min', network: 'TRON' },
    { id: 'TX-6B5C', type: 'BTC', amount: '+$4,100.00', status: 'completed', time: '18 min', network: 'Bitcoin' },
  ];

  const statusLabel = (s) => s === 'completed' ? t('dashboard.tx_completed') : t('dashboard.tx_pending');

  return (
    <section data-testid="dashboard-preview-section" className="py-14 sm:py-20 lg:py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-20 right-1/4 w-80 h-80 rounded-full bg-[hsl(var(--primary))] opacity-[0.07] blur-[100px] animate-float" />
        <div className="absolute bottom-10 left-1/4 w-64 h-64 rounded-full bg-[hsl(var(--neon-accent))] opacity-[0.06] blur-[80px] animate-float" style={{ animationDelay: '1.5s' }} />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Badge variant="outline" className="mb-3 border-[hsl(var(--primary-glow)/0.5)] text-[hsl(var(--primary-glow))] bg-[hsl(var(--primary)/0.15)] shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
              <BarChart3 className="w-3 h-3 mr-1.5" /> {t('dashboard.badge')}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
              <span className="text-gradient text-neon drop-shadow-[0_0_30px_hsl(var(--primary-glow)/0.6)]">{t('dashboard.title_line1')}</span>
              <br /><span className="text-white">{t('dashboard.title_line2')}</span>
            </h2>
            <p className="mt-2 text-white/75 max-w-xl text-sm sm:text-base">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--success))] opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[hsl(var(--success))] shadow-[0_0_8px_hsl(var(--success))]" />
            </span>
            <span className="text-xs text-[hsl(var(--success))] font-bold tracking-wide uppercase drop-shadow-[0_0_4px_hsl(var(--success)/0.5)]">{t('dashboard.live')}</span>
          </div>
        </div>
        <Card data-testid="dashboard-placeholder" className="p-0 overflow-hidden border-[hsl(var(--primary-glow)/0.3)] bg-[hsl(var(--surface)/0.9)] relative shadow-[0_0_40px_hsl(var(--primary)/0.12)]">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary-glow)/0.7)] to-transparent" />
          <div className="flex items-center justify-between px-5 py-3 border-b border-[hsl(var(--border)/0.5)] bg-[hsl(var(--background)/0.5)]">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <span className="text-xs text-white/40 font-mono">dashboard.fluxipay.com</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/50">
              <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> MAINNET</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 24h</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-[hsl(var(--warning))] drop-shadow-[0_0_4px_hsl(var(--warning))]" /> 4 ms</span>
            </div>
          </div>
          <div className="p-5 sm:p-6 space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {kpis.map((kpi) => {
                const c = colorMap[kpi.color];
                const Icon = kpi.icon;
                return (
                  <div key={kpi.label} className="relative rounded-xl p-4 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.4)] transition-all duration-300 hover:border-[hsl(var(--primary-glow)/0.5)] hover:shadow-[0_0_25px_hsl(var(--primary-glow)/0.2)] group">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-white/60 font-semibold tracking-wide">{kpi.label}</span>
                      <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center shadow-[0_0_12px_hsl(var(--primary)/0.15)]`}><Icon className={`w-4 h-4 ${c.text}`} /></div>
                    </div>
                    <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">{kpi.value}</div>
                    <div className="flex items-center gap-1 mt-1.5">
                      {kpi.trend === 'up' ? <TrendingUp className="w-3.5 h-3.5 text-[hsl(var(--success))] drop-shadow-[0_0_4px_hsl(var(--success))]" /> : <TrendingDown className="w-3.5 h-3.5 text-red-400 drop-shadow-[0_0_4px_rgba(248,113,113,0.5)]" />}
                      <span className={`text-xs font-semibold ${kpi.trend === 'up' ? 'text-[hsl(var(--success))]' : 'text-red-400'}`}>{kpi.change}</span>
                      <span className="text-xs text-white/30">{t('dashboard.vs_yesterday')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3 rounded-xl bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.4)] p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-sm font-bold text-white">{t('dashboard.chart_title')}</h3>
                    <p className="text-xs text-white/40 mt-0.5">{t('dashboard.chart_subtitle')}</p>
                  </div>
                  <div className="flex gap-1 p-0.5 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border)/0.3)]">
                    <button className="px-2.5 py-1 text-xs rounded-md bg-[hsl(var(--primary)/0.25)] text-[hsl(var(--primary-glow))] font-semibold shadow-[0_0_8px_hsl(var(--primary)/0.2)]">7D</button>
                    <button className="px-2.5 py-1 text-xs rounded-md text-white/40 hover:text-white/60 transition-colors duration-200">30D</button>
                    <button className="px-2.5 py-1 text-xs rounded-md text-white/40 hover:text-white/60 transition-colors duration-200">90D</button>
                  </div>
                </div>
                <div className="flex items-end gap-2 sm:gap-3" style={{ height: '192px' }}>
                  {chartValues.map((value, i) => {
                    const barHeight = Math.max(value * 1.6, 12);
                    const isHighlight = i === 3;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group/bar">
                        <div className={`w-full rounded-t-md relative cursor-pointer transition-all duration-300 ease-out ${isHighlight ? 'bg-gradient-to-t from-[hsl(var(--neon-accent))] to-[hsl(var(--primary-glow))]' : 'bg-gradient-to-t from-[hsl(var(--primary))] to-[hsl(var(--primary-glow))]'} group-hover/bar:shadow-[0_0_20px_hsl(var(--primary-glow)/0.5)]`}
                          style={{ height: `${barHeight}px`, opacity: isHighlight ? 1 : 0.6 + (value / 250) }}>
                          <div className={`absolute top-0 left-0 right-0 h-px ${isHighlight ? 'bg-[hsl(var(--neon-accent))]' : 'bg-[hsl(var(--primary-glow))]'} opacity-80`} />
                          <div className="absolute -top-9 left-1/2 -translate-x-1/2 opacity-0 group-hover/bar:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                            <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border)/0.5)] rounded-md px-2 py-1 text-xs text-white font-mono whitespace-nowrap shadow-lg">${(value * 72).toLocaleString()}</div>
                          </div>
                        </div>
                        <span className="text-[10px] text-white/50 font-medium mt-2">{days[i]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="lg:col-span-2 rounded-xl bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.4)] p-5 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-white">{t('dashboard.tx_title')}</h3>
                  <span className="text-xs text-[hsl(var(--primary-glow))] cursor-pointer hover:underline font-semibold">{t('dashboard.tx_view_all')}</span>
                </div>
                <div className="flex-1 space-y-2.5 overflow-hidden">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-[hsl(var(--surface)/0.5)] border border-[hsl(var(--border)/0.2)] hover:border-[hsl(var(--primary-glow)/0.3)] transition-all duration-200">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${tx.type === 'USDT' ? 'bg-[hsl(var(--success)/0.2)]' : tx.type === 'BTC' ? 'bg-[hsl(var(--warning)/0.2)]' : 'bg-[hsl(var(--primary-glow)/0.2)]'}`}>
                          <span className={`${cryptoColors[tx.type]} text-[10px] font-bold`}>{tx.type}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-white flex items-center gap-1.5">
                            <span className="font-mono text-white/70">{tx.id}</span>
                            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 h-4 ${statusBase[tx.status]}`}>{statusLabel(tx.status)}</Badge>
                          </div>
                          <div className="text-[10px] text-white/40 mt-0.5">{tx.network} · {t('dashboard.ago')} {tx.time}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-bold text-[hsl(var(--success))] drop-shadow-[0_0_4px_hsl(var(--success)/0.4)]">{tx.amount}</span>
                        <ArrowUpRight className="w-3 h-3 text-[hsl(var(--success)/0.7)]" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[hsl(var(--border)/0.3)]">
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--success))] animate-pulse shadow-[0_0_6px_hsl(var(--success))]" />
                  <span className="text-xs text-white/50">{t('dashboard.networks_active')}</span>
                </div>
                <div className="text-xs text-white/30">|</div>
                <span className="text-xs text-white/50">{t('dashboard.last_update')}: <span className="text-white/70 font-mono">{t('dashboard.ago')} 4s</span></span>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/40">
                <span>{t('dashboard.webhook_latency')}:</span>
                <span className="font-mono text-[hsl(var(--success))] font-bold">4ms</span>
                <div className="w-12 h-1.5 rounded-full bg-[hsl(var(--surface))] overflow-hidden">
                  <div className="h-full w-[95%] rounded-full bg-gradient-to-r from-[hsl(var(--success))] to-[hsl(var(--success)/0.6)]" />
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--neon-accent)/0.5)] to-transparent" />
        </Card>
      </div>
    </section>
  );
};

export default DashboardPreview;
